import React, { useEffect, useRef, useState } from 'react';
import WModal from 'shared_components/components/WModal';
import WButton from 'shared_components/components/WForms/WButton/WButton';
import {
  FIELD_ERROR_CLASS,
  FIELD_LABEL_CLASS,
  FIELD_LABEL_ROW_CLASS,
  FIELD_OPTIONAL_CLASS,
  MODAL_ACTIONS_CLASS,
  fieldControlClass,
} from './formStyles';
import {
  CONTRIBUTION_ALIAS_REQUIRED_ERROR,
  hasContributionAlias,
  resolveAliasIdByStageName,
} from '../../../utils/recordingValidators';
import {
  clampConfidenceCategory,
  confidenceCategoriesFor,
} from '../contributorRole';

/**
 * Contribution detail modal — the "Contribution Confidence" editor.
 *
 * Extracted from RecordingEditForm so the exact same modal can be reused by the
 * bulk-edit builder. It owns the Alias / Data Quality / Evidence / Notes form
 * and, on save, hands back a normalised payload
 * ({ contribution, confidenceCategory, confidencePattern, personaId }).
 *
 * The alias is required for *every* mode. It gates `add` for the obvious
 * reason, and it gates `edit` because the backend rewrites the contribution
 * edge with `source_id = persona_id` on an edit too — an edit sent without one
 * writes an edge with an empty source rather than failing (see
 * `hasContributionAlias`).
 *
 * The data-quality choice depends on the role: the "contributor" placeholder
 * may only be red or gray (see `contributorRole.ts`). The modal owns that
 * restriction rather than each opener, so it holds on every surface.
 */

/** Data quality categories (shared source of truth). */
export const CONFIDENCE_CATEGORIES: {
  id: string;
  label: string;
  color: string;
  textColor: string;
}[] = [
  {
    id: '1',
    label: 'Verified (No Effort)',
    color: '#6EE038',
    textColor: 'black',
  },
  {
    id: '2',
    label: 'Inferred (Low Effort)',
    color: '#F6FF00',
    textColor: 'black',
  },
  {
    id: '3',
    label: 'Credited (Mid Effort)',
    color: '#FF8A00',
    textColor: 'white',
  },
  {
    id: '4',
    label: 'Unverified (High Effort)',
    color: '#FF2600',
    textColor: 'white',
  },
  {
    id: '5',
    label: 'Unknown (Max Effort)',
    color: '#AAB2B9',
    textColor: 'black',
  },
];

const capitalize = (s: string) =>
  s ? s.charAt(0).toUpperCase() + s.slice(1) : s;

export interface ContributionAliasOption {
  stageName: string;
  personaId: string;
  /** The artist's main performing name (`persona.isMainPerformingName` in the
   *  graph). When flagged, it wins the default pick below. */
  isMain?: boolean;
}

/** The editable shape held by the form. */
export interface ContributionDetailValue {
  contributionName: string;
  confidenceCategory: string;
  /** Free text: where the evidence for this contribution was found (liner
   *  notes, a catalogue, a URL...). Named for the `sourceUrl` wire field it
   *  lands in; it is not held to a URL shape. */
  evidenceUrl: string;
  notes: string;
  personaId: string;
}

/** Normalised payload handed back on save (matches the recording contribution
 *  shape expected by the edit form / API). */
export interface ContributionSavePayload {
  contribution: string;
  confidenceCategory: string;
  confidencePattern: string;
  personaId?: string;
}

const EMPTY_VALUE: ContributionDetailValue = {
  contributionName: '',
  confidenceCategory: '1',
  evidenceUrl: '',
  notes: '',
  personaId: '',
};

interface ContributionDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Whether this opens a brand-new contribution or amends an existing one.
   *  Both require an alias; the mode only picks the wording. */
  mode: 'add' | 'edit';
  /** Seed values shown when the modal opens. */
  initialValue?: Partial<ContributionDetailValue>;
  aliases: ContributionAliasOption[];
  aliasesLoading?: boolean;
  /** The stage name credited on the recording being edited. Used only as a
   *  default-pick hint once the aliases load; surfaces without a single
   *  recording (bulk edit) simply omit it. */
  preferredStageName?: string;
  onSave: (payload: ContributionSavePayload) => void;
}

export const ContributionDetailModal: React.FC<
  ContributionDetailModalProps
> = ({
  isOpen,
  onClose,
  mode,
  initialValue,
  aliases,
  aliasesLoading = false,
  preferredStageName,
  onSave,
}) => {
  const [form, setForm] = useState<ContributionDetailValue>(EMPTY_VALUE);
  const [aliasError, setAliasError] = useState<string | null>(null);
  // Once the user has touched the alias picker themselves, the default pick
  // below must never overwrite their choice.
  const aliasTouchedRef = useRef(false);

  // Seed the form each time the modal opens. Intentionally only depends on
  // `isOpen` so re-renders while open don't clobber in-progress edits.
  const seedRef = useRef(initialValue);
  seedRef.current = initialValue;
  useEffect(() => {
    if (isOpen) {
      const seeded = { ...EMPTY_VALUE, ...seedRef.current };
      setForm({
        ...seeded,
        // A seed outside the role's allowed set (the openers seed '1') snaps
        // to the first allowed category, so the select never shows a value
        // the user could not have picked.
        confidenceCategory: clampConfidenceCategory(
          seeded.contributionName,
          seeded.confidenceCategory
        ),
      });
      setAliasError(null);
      aliasTouchedRef.current = false;
    }
  }, [isOpen]);

  // Default the alias once the async fetch lands. The seed above runs at open
  // time, when the opener's `lookup_client_aliases` fetch has usually not
  // resolved yet — on a first open the list is empty, so a blank seed stayed
  // blank and the user re-picked an alias on every save. A seeded
  // (previously used) alias already holds the field, so this only fills a
  // blank one: main alias first, then the persona credited on the recording,
  // then the first alias in the list. That last step is a fallback, not a
  // guess at the right persona: clients with several aliases and no main
  // one were landing on a blank picker (UAT), and the alias stays visible
  // and editable, so a wrong default is one click to fix while a blank
  // one is a blocked save.
  useEffect(() => {
    if (!isOpen || aliasesLoading || aliases.length === 0) return;
    if (aliasTouchedRef.current) return;
    setForm((prev) => {
      if (prev.personaId) return prev;
      const personaId =
        aliases.find((a) => a.isMain)?.personaId ||
        resolveAliasIdByStageName(preferredStageName, aliases) ||
        aliases[0].personaId;
      return { ...prev, personaId };
    });
  }, [isOpen, aliasesLoading, aliases, preferredStageName]);

  const allowedCategories = confidenceCategoriesFor(form.contributionName);
  const categoryOptions = allowedCategories
    ? CONFIDENCE_CATEGORIES.filter((cat) => allowedCategories.includes(cat.id))
    : CONFIDENCE_CATEGORIES;

  const handleSave = () => {
    if (!hasContributionAlias(form.personaId)) {
      setAliasError(CONTRIBUTION_ALIAS_REQUIRED_ERROR);
      return;
    }
    setAliasError(null);

    // Evidence is free text -- wherever the operator found it -- so beyond
    // trimming there is nothing to validate; blank is fine, the field is
    // optional.
    onSave({
      contribution: form.contributionName,
      confidenceCategory: clampConfidenceCategory(
        form.contributionName,
        form.confidenceCategory
      ),
      confidencePattern: JSON.stringify({
        evidenceUrl: form.evidenceUrl?.trim() ?? '',
        notes: form.notes?.trim() ?? '',
      }),
      personaId: form.personaId,
    });
  };

  return (
    <WModal
      isOpen={isOpen}
      onClose={onClose}
      title="Contribution"
      modalClasses="tw-max-w-md tw-w-full tw-mx-4"
    >
      <div className="tw-mt-4 tw-space-y-4">
        <div>
          <span className="tw-block tw-text-textSeal tw-text-xs tw-font-medium tw-mb-1">
            Contribution
          </span>
          <p className="tw-text-white tw-text-sm">
            {form.contributionName ? capitalize(form.contributionName) : '—'}
          </p>
        </div>
        <div>
          <label
            htmlFor="contribution-modal-alias"
            className={`${FIELD_LABEL_CLASS} tw-mb-2`}
          >
            Alias
          </label>
          <select
            id="contribution-modal-alias"
            value={form.personaId}
            onChange={(e) => {
              aliasTouchedRef.current = true;
              setForm((prev) => ({ ...prev, personaId: e.target.value }));
              if (aliasError) setAliasError(null);
            }}
            disabled={aliasesLoading}
            className={fieldControlClass({
              kind: 'select',
              hasValue: !!form.personaId,
              error: !!aliasError,
            })}
          >
            <option value="">
              {aliasesLoading ? 'Loading aliases…' : 'Select alias'}
            </option>
            {aliases.map((alias) => (
              <option key={alias.personaId} value={alias.personaId}>
                {alias.stageName}
              </option>
            ))}
          </select>
          {aliasError && (
            <p className={`${FIELD_ERROR_CLASS} tw-mt-1`}>{aliasError}</p>
          )}
        </div>
        <div>
          <label
            htmlFor="contribution-modal-confidence"
            className={`${FIELD_LABEL_CLASS} tw-mb-2`}
          >
            Data Quality Category
          </label>
          <select
            id="contribution-modal-confidence"
            value={form.confidenceCategory}
            onChange={(e) =>
              setForm((prev) => ({
                ...prev,
                confidenceCategory: e.target.value,
              }))
            }
            className={fieldControlClass({
              kind: 'select',
              hasValue: !!form.confidenceCategory,
            })}
          >
            {categoryOptions.map((cat) => (
              <option key={cat.id} value={cat.id}>
                {cat.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <div className={FIELD_LABEL_ROW_CLASS}>
            <label
              htmlFor="contribution-modal-evidence"
              className={FIELD_LABEL_CLASS}
            >
              Evidence
            </label>
            <span className={FIELD_OPTIONAL_CLASS}>Optional</span>
          </div>
          <input
            id="contribution-modal-evidence"
            type="text"
            value={form.evidenceUrl}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, evidenceUrl: e.target.value }))
            }
            placeholder="e.g. liner notes, a label catalogue, or a link"
            className={fieldControlClass()}
          />
        </div>
        <div>
          <div className={FIELD_LABEL_ROW_CLASS}>
            <label
              htmlFor="contribution-modal-notes"
              className={FIELD_LABEL_CLASS}
            >
              Notes
            </label>
            <span className={FIELD_OPTIONAL_CLASS}>Optional</span>
          </div>
          <textarea
            id="contribution-modal-notes"
            value={form.notes}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, notes: e.target.value }))
            }
            placeholder="Optional notes"
            rows={3}
            className={fieldControlClass({ kind: 'textarea' })}
          />
        </div>
        <div className={MODAL_ACTIONS_CLASS}>
          <WButton variant="secondary" label="Cancel" onClick={onClose} />
          <WButton
            variant="primary"
            label="Save"
            disabled={!hasContributionAlias(form.personaId)}
            onClick={handleSave}
          />
        </div>
      </div>
    </WModal>
  );
};

export default ContributionDetailModal;
